import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useCurrentAccount, useDisconnectWallet } from "@mysten/dapp-kit";
import { createSuiClient } from "@/lib/suiClient";
import { api } from "@/lib/api";
import { isHidden } from "@/lib/perf";

// Two contexts on purpose: identity/callbacks (stable) vs balances (change often). Address-only
// consumers read useWallet() and never re-render when a balance refreshes — this kills the
// re-render storm that made the app feel sluggish after connecting, especially on mobile.
const WalletContext = createContext(null);
const WalletBalanceContext = createContext(null);
export const useWallet = () => useContext(WalletContext);
export const useWalletBalance = () => useContext(WalletBalanceContext);

// Live SUI balance via Sui gRPC (same-origin gRPC-web bridge). Created once.
const directClient = createSuiClient();

export function WalletProvider({ children }) {
  const account = useCurrentAccount();
  const { mutate: disconnectWallet } = useDisconnectWallet();
  const rawAddress = account?.address || null;

  // DEBOUNCED address: the Slush in-app browser flaps useCurrentAccount() null↔address while it
  // reconnects. Committing those transient nulls makes every address-keyed consumer effect tear
  // down and refire, which is the real source of the connect lag. We hold the last real address
  // for ~400ms and only commit null if no real address returns within that window.
  const [address, setAddress] = useState(rawAddress);
  const nullTimerRef = useRef(null);
  useEffect(() => {
    if (rawAddress) {
      if (nullTimerRef.current) { clearTimeout(nullTimerRef.current); nullTimerRef.current = null; }
      setAddress((prev) => (prev === rawAddress ? prev : rawAddress));
    } else {
      if (nullTimerRef.current) clearTimeout(nullTimerRef.current);
      nullTimerRef.current = setTimeout(() => { setAddress(null); nullTimerRef.current = null; }, 400);
    }
  }, [rawAddress]);
  useEffect(() => () => { if (nullTimerRef.current) clearTimeout(nullTimerRef.current); }, []);
  const connected = !!address;

  const [tradingBalance, setTradingBalance] = useState(0);
  const [realBalance, setRealBalance] = useState(0);
  // Guards against the autoConnect reconnect churn (useCurrentAccount flaps null↔address) and
  // slow mobile RPC: never let balance reads stack, and provision the backend account only once
  // per real address — this is what kept firing continuous requests inside the Slush webview.
  const inflightRef = useRef(false);
  const provisionedRef = useRef(null);

  // Live on-chain SUI balance, fetched straight from the fullnode (not our backend).
  const refetchReal = useCallback(async () => {
    if (!address || inflightRef.current) return; // skip if a read is already in flight
    inflightRef.current = true;
    try {
      const { balance: b } = await directClient.getBalance({ owner: address });
      setRealBalance((prev) => {
        const next = Number(b.balance) / 1e9;
        return next === prev ? prev : next; // avoid a state update (re-render) when unchanged
      });
    } catch (e) {} finally { inflightRef.current = false; }
  }, [address]);

  // Poll the live balance on a 15s interval that is created ONCE per (debounced) address — keyed
  // on the stable address string, not on refetchReal, so a balance change never rebuilds it.
  const refetchRealRef = useRef(refetchReal);
  refetchRealRef.current = refetchReal;
  useEffect(() => {
    if (!address) { setRealBalance(0); return; }
    refetchRealRef.current();
    const t = setInterval(() => { if (!isHidden()) refetchRealRef.current(); }, 15_000);
    return () => clearInterval(t);
  }, [address]);

  const refreshTrading = useCallback(async (addr) => {
    const a = addr || address;
    if (!a) return;
    try {
      const { data } = await api.get(`/wallet/${a}`);
      setTradingBalance(data.sui_balance);
    } catch (e) {}
  }, [address]);

  // Ensure a backend trading account exists — ONCE per real address. provisionedRef is NOT reset
  // on a transient null (the reconnect window), so the null↔address flapping can't re-POST.
  useEffect(() => {
    if (!address) { setTradingBalance(0); return; }
    if (provisionedRef.current === address) return;
    provisionedRef.current = address;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post(`/wallet`, { address });
        if (alive) setTradingBalance(data.sui_balance);
      } catch (e) { if (provisionedRef.current === address) provisionedRef.current = null; }
    })();
    return () => { alive = false; };
  }, [address]);

  const disconnect = useCallback(() => disconnectWallet(), [disconnectWallet]);

  // Identity + stable callbacks only — this object's identity does NOT change when balances
  // update, so the many address-only consumers don't re-render on every balance poll/trade.
  const identity = useMemo(() => ({
    address, connected, disconnect, refresh: refreshTrading, refetchReal,
  }), [address, connected, disconnect, refreshTrading, refetchReal]);

  const balances = useMemo(() => ({
    realBalance, tradingBalance, balance: tradingBalance,
  }), [realBalance, tradingBalance]);

  return (
    <WalletContext.Provider value={identity}>
      <WalletBalanceContext.Provider value={balances}>
        {children}
      </WalletBalanceContext.Provider>
    </WalletContext.Provider>
  );
}
